import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import API from "../lib/api";
import { Section } from "../components/Common";
import AppCta from "../components/AppCta";
import {
  ShoppingBag,
  Clock,
  Tag,
  ExternalLink,
  Sparkles,
  BellPlus,
  Truck,
} from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "../lib/errors";

// ---------- Availability chip -------------------------------------------------
//
// Never shows a fake Buy state. Maps 1:1 to shop_categories.compute_availability_state.
//
function AvailabilityChip({ state, isDropship }) {
  const { t } = useTranslation();
  const map = {
    buy_now: {
      cls: "bg-[#C8A24A]/15 text-[#E2C77A] border-[#C8A24A]/35",
      label: t("shop.state_buy_now", "Buy now"),
      icon: <ShoppingBag className="w-3 h-3" />,
    },
    request: {
      cls: "bg-white/[0.06] text-white/85 border-white/20",
      label: t("shop.state_request", "Request"),
      icon: <Sparkles className="w-3 h-3" />,
    },
    dropship: {
      cls: "bg-white/[0.05] text-white/80 border-white/15",
      label: t("shop.state_dropship", "Dropship"),
      icon: <Truck className="w-3 h-3" />,
    },
    coming_soon: {
      cls: "bg-white/[0.04] text-white/70 border-white/12",
      label: t("shop.state_coming_soon", "Coming soon"),
      icon: <Clock className="w-3 h-3" />,
    },
    unavailable: {
      cls: "bg-white/[0.03] text-white/55 border-white/10",
      label: t("shop.state_coming_soon", "Coming soon"),
      icon: <Clock className="w-3 h-3" />,
    },
  };
  const entry = map[state] || map.coming_soon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] tracking-[0.14em] uppercase ${entry.cls}`}
      data-testid={`chip-state-${state}`}
    >
      {entry.icon}
      {entry.label}
      {isDropship && state === "buy_now" && (
        <span className="ml-1 text-white/60 normal-case tracking-normal">
          · {t("shop.dropship_note", "partner-fulfilled")}
        </span>
      )}
    </span>
  );
}

// ---------- Waitlist mini-form (inline for empty subcategories) -------------

function WaitlistCTA({ topLevel, categoryKey, categoryLabel, testid }) {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error(t("common.required_fields"));
    setSending(true);
    try {
      await API.post("/shop/waitlist", {
        email,
        category: categoryKey,
        top_level: topLevel,
        language: i18n.language,
      });
      setSent(true);
      setEmail("");
      toast.success(t("shop.waitlist_sent", "You're on the waitlist."));
    } catch (e) {
      toast.error(errorMessage(e, t("shop.waitlist_failed", "Could not join the waitlist.")));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="text-xs text-white/70" data-testid={`waitlist-sent-${categoryKey}`}>
        {t("shop.waitlist_sent_hint", "We'll email you when {{cat}} launches.", { cat: categoryLabel })}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2" data-testid={testid}>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t("shop.waitlist_email_ph", "your@email.com")}
        required
        className="bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm flex-1 min-w-0"
        data-testid={`waitlist-email-${categoryKey}`}
      />
      <button
        type="submit"
        disabled={sending}
        className="btn-secondary text-sm inline-flex items-center gap-2 whitespace-nowrap disabled:opacity-40"
        data-testid={`waitlist-submit-${categoryKey}`}
      >
        <BellPlus className="w-4 h-4" />
        {sending ? "\u2026" : t("shop.waitlist_cta", "Notify me")}
      </button>
    </form>
  );
}

// ---------- Product card (single source, all top-levels) --------------------

function ProductCard({ product, onBuy, buying }) {
  const { t } = useTranslation();
  const isPersonalised = product.kind === "personalised";
  const state = product.availability_state || "coming_soon";

  const canBuyNow = state === "buy_now" && !isPersonalised;
  const canRequest = state === "request" || isPersonalised;

  const priceLabel =
    product.price_eur > 0
      ? `€${Number(product.price_eur || 0).toFixed(2)}`
      : null;

  return (
    <div
      className="card-surface overflow-hidden flex flex-col"
      data-testid={`product-${product.id}`}
    >
      <div className="aspect-[4/3] overflow-hidden bg-white/[0.04]">
        {product.image ? (
          <img
            src={product.image}
            alt={product.image_alt || product.name}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/25 text-xs uppercase tracking-[0.24em]">
            {t("shop.image_pending", "Image coming soon")}
          </div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="chip">
            <Tag className="w-3 h-3" />
            {t(`shop.${product.kind}`, product.kind)}
          </span>
          <AvailabilityChip state={state} isDropship={product.is_dropship} />
          {product.vendor && (
            <span className="text-[10px] tracking-[0.18em] uppercase text-white/45">
              {product.vendor}
            </span>
          )}
        </div>

        <div className="font-display text-xl leading-snug mt-1">{product.name}</div>
        <div className="text-sm text-white/65 flex-1">
          {product.summary || product.description}
        </div>

        <div className="flex items-center justify-between mt-3 gap-3">
          {priceLabel && !isPersonalised ? (
            <div className="font-display text-2xl gold-text whitespace-nowrap">
              {priceLabel}
            </div>
          ) : (
            <div
              className="text-xs text-white/50 tracking-[0.18em] uppercase"
              data-testid={`price-personalised-${product.id}`}
            >
              {t("shop.on_request", "On request")}
            </div>
          )}

          {canBuyNow ? (
            <button
              onClick={() => onBuy(product)}
              disabled={buying === product.id}
              className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-40"
              data-testid={`buy-${product.id}`}
            >
              {product.source === "shopify" ? (
                <ExternalLink className="w-4 h-4" />
              ) : (
                <ShoppingBag className="w-4 h-4" />
              )}
              {buying === product.id
                ? "\u2026"
                : product.source === "shopify"
                  ? t("shop.checkout_on_shopify", "Buy on Shopify")
                  : t("shop.buy", "Buy")}
            </button>
          ) : canRequest ? (
            <Link
              to={product.quote_url || `/shop/quote?product=${product.personalised_key || product.id}`}
              className="btn-secondary text-sm inline-flex items-center gap-2 whitespace-nowrap"
              data-testid={`request-${product.id}`}
            >
              <Sparkles className="w-4 h-4" />
              {t("shop.request_quote_cta", "Request a quote")}
            </Link>
          ) : (
            <span
              className="text-xs text-white/50 tracking-[0.14em] uppercase"
              data-testid={`unavailable-${product.id}`}
            >
              {t("shop.state_coming_soon", "Coming soon")}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Sub-category tile (empty state) ---------------------------------

function EmptyCategoryTile({ sub, topLevel }) {
  const { t } = useTranslation();
  return (
    <div
      className="card-surface p-5 flex flex-col gap-3"
      data-testid={`empty-cat-${sub.key}`}
    >
      <div className="flex items-center justify-between">
        <div className="font-display text-lg">{sub.label}</div>
        <AvailabilityChip state="coming_soon" />
      </div>
      <div className="text-sm text-white/65 leading-relaxed">{sub.blurb}</div>
      <div className="text-xs uppercase tracking-[0.22em] gold-text pt-1">
        {t("shop.waitlist_eyebrow", "Join the waitlist")}
      </div>
      <WaitlistCTA
        topLevel={topLevel}
        categoryKey={sub.key}
        categoryLabel={sub.label}
        testid={`waitlist-form-${sub.key}`}
      />
    </div>
  );
}

// ---------- Main page -------------------------------------------------------

export default function ShopPage() {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState("digital");
  const [subFilter, setSubFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const [tree, setTree] = useState(null);

  // Load the category tree once (drives subcategory tiles + counts)
  useEffect(() => {
    API.get("/shop/categories")
      .then(({ data }) => setTree(data))
      .catch(() => setTree(null));
  }, []);

  // Load products for the current tab
  useEffect(() => {
    setLoading(true);
    setSubFilter("all");
    API.get(`/shop/products?kind=${tab}`)
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }, [tab]);

  const subs = tree?.physical_subcategories || [];
  const physicalSubsWithProducts = subs.filter((s) => s.has_products);

  // Filter products by active sub-category (physical only)
  const visibleItems = useMemo(() => {
    if (tab !== "physical" || subFilter === "all") return items;
    return items.filter((p) => p.subcategory === subFilter);
  }, [tab, subFilter, items]);

  const buy = async (p) => {
    if (buying) return;
    setBuying(p.id);
    try {
      if (p.kind === "digital") {
        // Existing Stripe Checkout flow — untouched
        const { data } = await API.post("/shop/checkout", {
          product_id: p.id,
          origin_url: window.location.origin,
          language: i18n.language,
        });
        window.location.assign(data.url);
        return;
      }
      if (p.source === "shopify") {
        const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        window.location.assign(
          `${base}/api/shop/physical/buy/${encodeURIComponent(p.id)}`
        );
        return;
      }
      toast.info(
        t("shop.physical_coming_soon", "Physical commerce launches once Shopify is connected.")
      );
    } catch (e) {
      toast.error(errorMessage(e, t("shop.checkout_failed", "Checkout failed")));
    } finally {
      setBuying(null);
    }
  };

  // Sub-categories to render on the Physical tab.
  // For every non-empty subcategory we show a chip; for empty ones we
  // render dedicated Coming-soon tiles below the product grid.
  const emptySubcategories = subs.filter((s) => !s.has_products);

  return (
    <div data-testid="page-shop">
      <Section
        eyebrow={t("shop.eyebrow")}
        title={t("shop.title")}
        subtitle={t("shop.subtitle")}
      >
        {/* Top-level tabs: Digital / Physical / Personalised */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="shop-tabs">
          <button
            onClick={() => setTab("digital")}
            className={`chip ${tab === "digital" ? "chip-active" : ""}`}
            data-testid="tab-digital"
          >
            {t("shop.digital", "Digital")}
          </button>
          <button
            onClick={() => setTab("physical")}
            className={`chip ${tab === "physical" ? "chip-active" : ""}`}
            data-testid="tab-physical"
          >
            {t("shop.physical", "Physical")}
          </button>
          <button
            onClick={() => setTab("personalised")}
            className={`chip ${tab === "personalised" ? "chip-active" : ""}`}
            data-testid="tab-personalised"
          >
            {t("shop.personalised", "Personalised")}
          </button>
        </div>

        {/* Physical subcategory chip row (only when there is at least one) */}
        {tab === "physical" && physicalSubsWithProducts.length > 0 && (
          <div
            className="flex flex-wrap gap-2 mb-6"
            data-testid="shop-subcategory-chips"
          >
            <button
              onClick={() => setSubFilter("all")}
              className={`chip ${subFilter === "all" ? "chip-active" : ""}`}
              data-testid="subcat-all"
            >
              {t("shop.all", "All")}
            </button>
            {physicalSubsWithProducts.map((s) => (
              <button
                key={s.key}
                onClick={() => setSubFilter(s.key)}
                className={`chip ${subFilter === s.key ? "chip-active" : ""}`}
                data-testid={`subcat-${s.key}`}
              >
                {s.label} <span className="text-white/50 ml-1">· {s.product_count}</span>
              </button>
            ))}
          </div>
        )}

        {/* Loading skeleton */}
        {loading ? (
          <div
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="shop-skeleton"
          >
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="card-surface overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-white/[0.04]" />
                <div className="p-4 space-y-2">
                  <div className="h-3 w-16 bg-white/[0.06] rounded" />
                  <div className="h-5 w-3/4 bg-white/[0.06] rounded" />
                  <div className="h-3 w-full bg-white/[0.04] rounded" />
                  <div className="h-3 w-2/3 bg-white/[0.04] rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Product grid */}
            {visibleItems.length > 0 ? (
              <div
                className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
                data-testid="shop-grid"
              >
                {visibleItems.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    onBuy={buy}
                    buying={buying}
                  />
                ))}
              </div>
            ) : (
              <div
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center"
                data-testid="shop-empty-tab"
              >
                <div className="font-display text-2xl mb-2">
                  {tab === "personalised"
                    ? t("shop.personalised_empty_title", "Personalised orders coming soon")
                    : t("shop.tab_empty_title", "Products arriving soon")}
                </div>
                <p className="text-sm text-white/65 max-w-md mx-auto">
                  {tab === "personalised"
                    ? t(
                        "shop.personalised_empty_sub",
                        "Bespoke and made-to-order products will appear here."
                      )
                    : t(
                        "shop.tab_empty_sub",
                        "We're finalising this catalogue. Explore the other tabs while we prepare it."
                      )}
                </p>
              </div>
            )}
          </>
        )}

        {/* Coming-soon subcategories (waitlist) — only on Physical tab */}
        {tab === "physical" && emptySubcategories.length > 0 && (
          <div className="mt-14" data-testid="shop-coming-soon-block">
            <div className="text-xs uppercase tracking-[0.28em] gold-text mb-3">
              {t("shop.coming_soon_eyebrow", "— Arriving in the ecosystem")}
            </div>
            <h3 className="font-display text-2xl mb-1 max-w-[26ch]">
              {t(
                "shop.coming_soon_title",
                "New categories, released as we curate them."
              )}
            </h3>
            <p className="text-sm text-white/65 mb-6 max-w-[52ch]">
              {t(
                "shop.coming_soon_sub",
                "Join the waitlist below and we'll email you the moment a category opens."
              )}
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {emptySubcategories.map((sub) => (
                <EmptyCategoryTile
                  key={sub.key}
                  sub={sub}
                  topLevel="physical"
                />
              ))}
            </div>
          </div>
        )}

        {/* Personalised tab: elegant CTA card linking to the quote page */}
        {tab === "personalised" && visibleItems.length > 0 && (
          <div
            className="mt-10 card-surface p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
            data-testid="personalised-cta-block"
          >
            <div>
              <div className="text-xs uppercase tracking-[0.28em] gold-text mb-2">
                {t("shop.personalised_eyebrow", "— Made to order")}
              </div>
              <div className="font-display text-2xl leading-snug">
                {t("shop.personalised_cta_title", "Bespoke, bulk or event orders.")}
              </div>
              <p className="text-sm text-white/65 max-w-[52ch] mt-1">
                {t(
                  "shop.personalised_cta_sub",
                  "Send us your brief and we'll come back with a tailored quote and lead time."
                )}
              </p>
            </div>
            <Link
              to="/shop/quote?product=edible_cups"
              className="btn-primary text-sm inline-flex items-center gap-2 whitespace-nowrap"
              data-testid="personalised-cta-button"
            >
              <Sparkles className="w-4 h-4" />
              {t("shop.request_quote_cta", "Request a quote")}
            </Link>
          </div>
        )}
      </Section>

      <AppCta variant="shop" testid="shop-app-cta" />
    </div>
  );
}
